import { computed, onBeforeUnmount, ref, Ref, set } from "@vue/composition-api";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EVENTS } from "@/const";

interface MessageThreadData {
  opponentId?: string;
  ownerId?: string;
  threadId?: string;
}

export default function useMessageThread(data: Ref<MessageThreadData>) {
  const messageThread = ref<MessageThread>();
  const processingPin = ref(false);

  const opponent = computed<UserPublicData | null>(
    () =>
      messageThread.value?.users.find(
        i =>
          (data.value.opponentId && i.id === data.value.opponentId) ||
          (data.value.ownerId && i.id !== data.value.ownerId)
      ) || null
  );
  const minUnreadMessageId = computed(() => messageThread.value?.minUnreadMessageId ?? "0");
  const opponentMinUnreadMessageId = computed(() => messageThread.value?.opponentMinUnreadMessageId ?? "0");

  const getThreadByOpponent = async (opponentId: string) => {
    try {
      providers.MessagesProvider.setReceiveErrors(false);
      messageThread.value = await providers.MessageThreadsProvider.findPersonal({ opponentId });
    } catch (e) {}
  };

  const getThreadById = async (threadId: string) => {
    try {
      providers.MessagesProvider.setReceiveErrors(false);
      const response = await providers.MessageThreadsProvider.find({ data: { ids: [threadId] } });
      messageThread.value = response.result[0];
    } catch (e) {}
  };

  const updateThread = async (thread: MessageThread) => {
    if (thread.id === messageThread.value?.id) messageThread.value = thread;
  };

  const toggleHideThread = async () => {
    if (!messageThread.value) return;
    const response = await providers.MessageThreadsProvider.hide({ id: messageThread.value.id });
    set(messageThread.value, "hidden", response ? !messageThread.value?.hidden : null);
  };

  const togglePin = async () => {
    if (!messageThread.value || processingPin.value) return;
    try {
      processingPin.value = true;
      const pinned = await providers.MessageThreadsProvider.togglePin({ threadId: messageThread.value.id });
      emitter.emit(EVENTS.NOTIFICATION_SHOW, {
        text: pinned ? $t("Marked as important") : $t("Marked as common"),
        timeout: 2000,
      });
      set(messageThread.value, "pinTime", pinned ? new Date().toISOString() : null);
    } finally {
      processingPin.value = false;
    }
  };

  emitter.on<any>(EVENTS.MESSAGES_UPDATE_THREAD, updateThread);

  onBeforeUnmount(() => {
    emitter.off<any>(EVENTS.MESSAGES_UPDATE_THREAD, updateThread);
  });

  return {
    messageThread,
    opponent,
    minUnreadMessageId,
    opponentMinUnreadMessageId,
    processingPin,
    getThreadByOpponent,
    getThreadById,
    toggleHideThread,
    togglePin,
  };
}
